import { useState, useEffect, useCallback } from 'react';
import { 
  reactionsService, 
  ItemReaction, 
  ItemVote, 
  VotingAnalytics, 
  ReactionSummary,
  AddReactionRequest,
  AddVoteRequest,
  VoteType,
  ReactionType,
  REACTION_TYPES 
} from '@/services/reactionsService';
import { useSharedCart } from '@/components/shared-cart/SharedCartProvider';

export interface UseReactionsReturn {
  // Reaction state
  reactions: ItemReaction[];
  reactionSummary: ReactionSummary | null;
  userReaction: ItemReaction | null;
  
  // Voting state
  votes: ItemVote[];
  votingAnalytics: VotingAnalytics | null;
  userVote: ItemVote | null;
  
  // Loading states
  loading: boolean;
  error: string | null;
  
  // Actions
  addReaction: (reactionType: ReactionType) => Promise<void>;
  removeReaction: () => Promise<void>;
  addVote: (voteType: VoteType, strength: number, comment?: string, isAnonymous?: boolean) => Promise<void>;
  removeVote: () => Promise<void>;
  refreshData: () => Promise<void>;
  
  // Real-time features
  isConnected: boolean;
}

export function useReactions(cartId: string, itemId: string, currentUserId?: string): UseReactionsReturn {
  const [reactions, setReactions] = useState<ItemReaction[]>([]);
  const [reactionSummary, setReactionSummary] = useState<ReactionSummary | null>(null);
  const [votes, setVotes] = useState<ItemVote[]>([]);
  const [votingAnalytics, setVotingAnalytics] = useState<VotingAnalytics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { sendMessage, isConnected, addMessageListener } = useSharedCart();

  // Get user's current reaction and vote
  const userReaction = reactions.find(r => 
    (currentUserId && r.userId === currentUserId) || 
    (!currentUserId && r.guestId)
  ) || null;

  const userVote = votes.find(v => 
    (currentUserId && v.userId === currentUserId) || 
    (!currentUserId && v.guestId)
  ) || null;

  // Load all data for the item
  const refreshData = useCallback(async () => {
    if (!cartId || !itemId) return;

    setLoading(true);
    setError(null);

    try {
      const [
        reactionsData,
        reactionSummaryData,
        votesData,
        analyticsData
      ] = await Promise.all([
        reactionsService.getItemReactions(cartId, itemId),
        reactionsService.getReactionSummary(cartId, itemId),
        reactionsService.getItemVotes(cartId, itemId),
        reactionsService.getVotingAnalytics(cartId, itemId)
      ]);

      setReactions(reactionsData);
      setReactionSummary(reactionSummaryData);
      setVotes(votesData);
      setVotingAnalytics(analyticsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load reactions and votes');
      console.error('Failed to load reactions and votes:', err);
    } finally {
      setLoading(false);
    }
  }, [cartId, itemId]);

  // Add or update reaction
  const addReaction = useCallback(async (reactionType: ReactionType) => {
    if (!cartId || !itemId) return;

    try {
      const request: AddReactionRequest = {
        userId: currentUserId,
        reactionType,
        emoji: REACTION_TYPES[reactionType].emoji,
      };

      const newReaction = await reactionsService.addReaction(cartId, itemId, request);
      
      // Optimistically update local state
      setReactions(prev => {
        const filtered = prev.filter(r => 
          (currentUserId && r.userId !== currentUserId) || 
          (!currentUserId && r.guestId !== request.guestId)
        );
        return [...filtered, newReaction];
      });

      // Send real-time update
      if (isConnected) {
        sendMessage('item_reaction_added', {
          itemId,
          reaction: newReaction,
          reactionType,
          emoji: REACTION_TYPES[reactionType].emoji,
        });
      }

      // Refresh summary data
      setTimeout(() => {
        reactionsService.getReactionSummary(cartId, itemId)
          .then(setReactionSummary)
          .catch(console.error);
      }, 100);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add reaction');
      throw err;
    }
  }, [cartId, itemId, currentUserId, isConnected, sendMessage]);

  // Remove reaction
  const removeReaction = useCallback(async () => {
    if (!cartId || !itemId || !userReaction) return;

    try {
      await reactionsService.removeReaction(cartId, itemId, userReaction.guestId);
      
      // Optimistically update local state
      setReactions(prev => prev.filter(r => r.id !== userReaction.id));

      // Send real-time update
      if (isConnected) {
        sendMessage('item_reaction_removed', {
          itemId,
          reactionId: userReaction.id,
          reactionType: userReaction.reactionType,
        });
      }

      // Refresh summary data
      setTimeout(() => {
        reactionsService.getReactionSummary(cartId, itemId)
          .then(setReactionSummary)
          .catch(console.error);
      }, 100);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove reaction');
      throw err;
    }
  }, [cartId, itemId, userReaction, isConnected, sendMessage]);

  // Add or update vote
  const addVote = useCallback(async (
    voteType: VoteType, 
    strength: number = 1, 
    comment?: string, 
    isAnonymous: boolean = false
  ) => {
    if (!cartId || !itemId) return;

    try {
      const request: AddVoteRequest = {
        userId: currentUserId,
        voteType,
        voteStrength: strength,
        comment,
        isAnonymous,
      };

      const newVote = await reactionsService.addVote(cartId, itemId, request);
      
      // Optimistically update local state
      setVotes(prev => {
        const filtered = prev.filter(v => 
          (currentUserId && v.userId !== currentUserId) || 
          (!currentUserId && v.guestId !== request.guestId)
        );
        return [...filtered, newVote];
      });

      // Send real-time update
      if (isConnected) {
        sendMessage('item_vote_added', {
          itemId,
          vote: newVote,
          voteType,
          voteStrength: strength,
          isAnonymous,
        });
      }

      // Refresh analytics data
      setTimeout(() => {
        reactionsService.getVotingAnalytics(cartId, itemId)
          .then(setVotingAnalytics)
          .catch(console.error);
      }, 100);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add vote');
      throw err;
    }
  }, [cartId, itemId, currentUserId, isConnected, sendMessage]);

  // Remove vote
  const removeVote = useCallback(async () => {
    if (!cartId || !itemId || !userVote) return;

    try {
      await reactionsService.removeVote(cartId, itemId, userVote.guestId);
      
      // Optimistically update local state
      setVotes(prev => prev.filter(v => v.id !== userVote.id));

      // Send real-time update
      if (isConnected) {
        sendMessage('item_vote_removed', {
          itemId,
          voteId: userVote.id,
          voteType: userVote.voteType,
        });
      }

      // Refresh analytics data
      setTimeout(() => {
        reactionsService.getVotingAnalytics(cartId, itemId)
          .then(setVotingAnalytics)
          .catch(console.error);
      }, 100);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove vote');
      throw err;
    }
  }, [cartId, itemId, userVote, isConnected, sendMessage]);

  // Load data on mount and when dependencies change
  useEffect(() => {
    refreshData();
  }, [refreshData]);

  // Refresh when other participants react or vote on this item
  useEffect(() => {
    const unsubscribe = addMessageListener((message) => {
      switch (message.type) {
        case 'item_reaction_added':
        case 'item_reaction_updated':
        case 'item_reaction_removed':
        case 'item_vote_added':
        case 'item_vote_updated':
        case 'item_vote_removed':
        case 'voting_analytics_updated':
          if (message.data?.itemId === itemId) {
            refreshData();
          }
          break;
      }
    });

    return unsubscribe;
  }, [addMessageListener, itemId, refreshData]);

  return {
    // State
    reactions,
    reactionSummary,
    userReaction,
    votes,
    votingAnalytics,
    userVote,
    loading,
    error,
    
    // Actions
    addReaction,
    removeReaction,
    addVote,
    removeVote,
    refreshData,
    
    // Real-time
    isConnected,
  };
}